import * as Yup from "yup";
import { UploadDocumentDocumentFormatEnum } from "@/services";
import {
  FAX_NUMBER_LENGTH_MESSAGE,
  FAX_NUMBER_PATTERN,
} from "@/constants/validation-constant";

const SENDER_NAME_PATTERN = /^[A-Za-z]+(?:[\s'-][A-Za-z]+)*$/;

/* SRFax caps the cover-page sender name at 30 characters. */
const SENDER_NAME_MAX_LENGTH = 30;

/* SRFax only carries these four; anything else reaches the API and comes back
   as a raw provider error, so the form rejects it up front. */
export const FAX_ALLOWED_FILE_PATTERN = /\.(pdf|png|jpe?g|gif)$/i;

export const FAX_SUPPORTED_FILE_LABEL = "JPEG, PNG, GIF, PDF";

const SENDER_NAME_FORMAT_MESSAGE =
  "Sender Name should only contain alphabetic characters.";

const SENDER_NAME_LENGTH_MESSAGE = `Sender Name cannot be more than ${SENDER_NAME_MAX_LENGTH} characters.`;

const FAX_FILE_REQUIRED_MESSAGE = "Please upload a document to fax.";

const FAX_FILE_FORMAT_MESSAGE =
  "Unsupported file format. Please upload a JPEG, PNG, GIF or PDF file.";

export const FAX_FILE_READ_ERROR_MESSAGE =
  "Could not read that file. Please try again.";

/* `name` and `sFileName_2` hold the chosen file names; the files themselves are
   already base64 by the time Yup runs, so the extension is what identifies them. */
export const FAX_DOCUMENT_VALIDATION_SCHEMA = Yup.object({
  documentType: Yup.string().trim().required().label("Document Type"),
  sCPFromName: Yup.string()
    .trim()
    .max(SENDER_NAME_MAX_LENGTH, SENDER_NAME_LENGTH_MESSAGE)
    .matches(SENDER_NAME_PATTERN, {
      message: SENDER_NAME_FORMAT_MESSAGE,
      excludeEmptyString: true,
    })
    .label("Sender Name"),
  toFaxNumber: Yup.string()
    .trim()
    .required()
    .matches(FAX_NUMBER_PATTERN, FAX_NUMBER_LENGTH_MESSAGE)
    .label("To Fax Number"),
  name: Yup.string()
    .required(FAX_FILE_REQUIRED_MESSAGE)
    .matches(FAX_ALLOWED_FILE_PATTERN, {
      message: FAX_FILE_FORMAT_MESSAGE,
      excludeEmptyString: true,
    }),
  sFileName_2: Yup.string().matches(FAX_ALLOWED_FILE_PATTERN, {
    message: FAX_FILE_FORMAT_MESSAGE,
    excludeEmptyString: true,
  }),
});

export const DOCUMENT_TYPES = [
  { label: "Single", value: "SINGLE" },
  { label: "Broadcast", value: "BROADCAST" },
];
// export const DOCUMENT_TABLE_MAPPER: Record<string, string> = {
//   PATIENT_REGISTRATION_FORMS: "Patient Registration Forms",
//   INSURANCE_INFORMATION: "Insurance Information",
//   CONSENT_FORMS: "Consent Forms",
//   ID_AND_PROOF_OF_ADDRESS: "Id And Proof Of Address",
//   PROGRESS_NOTES: "Progress Notes",
//   LAB_REPORTS: "Lab Reports",
//   IMAGING_REPORTS: "Imaging Reports",
//   CONSULTATION_NOTES: "Consultation Notes",
//   SURGERY_REPORTS: "Surgery Reports",
//   DISCHARGE_SUMMARIES: "Discharge Summaries",
//   MEDICATION_LISTS: "Medication Lists",
//   REFERRAL_LETTERS: "Referral Letters",
//   AUTHORIZATION_LETTERS: "Authorization Letters",
// };

export type DocumentForm = {
  name: string;
  documentType?: string;
  documentFormat: UploadDocumentDocumentFormatEnum;
  file?: File;
  documentDate?: string;
  documentDescription?: string;
  toFaxNumber?: string;
  sCPFromName?: string;
  sFileName_2?: string;
  sFileContent_2?: string;
};

export const DOCUMENT_INITIAL_VALUE: DocumentForm = {
  name: "",
  documentType: "SINGLE",
  documentFormat: "PDF",
  file: undefined,
  documentDescription: "",
  documentDate: "",
  toFaxNumber: "",
  sCPFromName: "",
  sFileName_2: "",
  sFileContent_2: undefined,
};

export const DOCUMENT_FORMAT_MAPPER: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "MS_WORD",
  "application/zip": "ZIP",
  "application/x-gzip": "X_GZIP",
  "application/x-zip-compressed": "ZIP",
  "application/x-compressed": "X_COMPRESSED",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "text/rtf": "RTF",
  "text/plain": "PLAIN",
  "image/jpeg": "JPEG_IMAGE",
  "image/png": "PNG_IMAGE",
  "image/gif": "GIF_IMAGE",
  "image/bmp": "BMP_IMAGE",
  "image/webp": "WEBP_IMAGE",
  "image/svg+xml": "SVG_IMAGE",
  "audio/mpeg": "MPEG_AUDIO",
  "video/mp4": "MP4",
  "binary/octet-stream": "OTHER",
  "text/csv": "CSV",
};
